import { useState, useEffect, useRef } from 'react'
import {
  Modal,
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Group,
  Anchor,
  Center,
  rem,
  Code,
  ActionIcon,
  Box,
  UnstyledButton,
  useMantineTheme
} from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { z } from 'zod'
import { TbCheck } from 'react-icons/tb'
import { ReactComponent as IcArrowRight } from '@/assets/icon/ic_arrow_right.svg'
import { ReactComponent as IcX } from '@/assets/icon/ic_x.svg'
import { useUserContext } from '@/context/UserContext'
import styles from './AuthModal.module.css'
import { showSuccessNotification } from '@/constants/notifications'

interface AuthModalProps {
  opened: boolean
  onClose: () => void
  initialStep?: AuthStep
  resetParams?: ResetPasswordParams
}

type AuthStep = 'email' | 'enter_password' | 'create_password' | 'forgot_password' | 'create_new_password' | 'email_sent'

interface ResetPasswordParams {
  token: string
  username: string
}

export const AuthModal = ({ opened, onClose, initialStep, resetParams }: AuthModalProps) => {
  const theme = useMantineTheme()
  const [currentStep, setCurrentStep] = useState<AuthStep>(initialStep || 'email')
  const [localResetParams, setLocalResetParams] = useState<ResetPasswordParams | null>(resetParams || null)
  const { login, signup, sendResetPasswordEmail, checkExists, loading, resetPassword } = useUserContext()

  useEffect(() => {
    if (initialStep) {
      setCurrentStep(initialStep)
    }
  }, [initialStep])

  useEffect(() => {
    if (resetParams) {
      setLocalResetParams(resetParams)
    }
  }, [resetParams])

  // Function to determine the previous step for back button navigation
  const getPreviousStep = (): AuthStep => {
    switch (currentStep) {
      case 'enter_password':
      case 'create_password':
        return 'email'
      case 'forgot_password':
        return 'enter_password'
      case 'email_sent':
        return 'forgot_password'
      default:
        return 'email'
    }
  }

  // Function to handle back button click
  const handleBack = () => {
    setCurrentStep(getPreviousStep())
  }

  // Function to check if current step should show X button
  const shouldShowCloseButton = (): boolean => {
    return currentStep === 'email' || currentStep === 'create_new_password'
  }

  // Function to check if current step should show back button
  const shouldShowBackButton = (): boolean => {
    return ['enter_password', 'create_password', 'forgot_password', 'email_sent'].includes(currentStep)
  }

  const getValidationSchema = (step: AuthStep) => {
    switch (step) {
      case 'email':
      case 'forgot_password':
        return z.object({
          email: z.string().email('Please enter a valid email'),
          password: z.string().optional()
        })
      case 'enter_password':
      case 'create_password':
        return z.object({
          email: z.string().email('Please enter a valid email').optional(),
          password: z.string().min(8, 'Minimum 8 characters required')
        })
      case 'create_new_password':
        return z.object({
          password: z.string().min(8, 'Minimum 8 characters required')
        })
      default:
        return z.object({
          email: z.string().email('Please enter a valid email'),
          password: z.string().optional()
        })
    }
  }

  const form = useForm({
    initialValues: {
      email: '',
      password: ''
    },
    validate: zodResolver(getValidationSchema(currentStep))
  })

  // Reset form values when step changes
  useEffect(() => {
    if (currentStep === 'create_new_password') {
      form.setValues({
        password: ''
      })
    }
  }, [currentStep])

  const handleNext = async () => {
    try {
      // Validate form before proceeding
      const validation = form.validate()
      if (validation.hasErrors) {
        return
      }

      switch (currentStep) {
        case 'email':
          const exists = await checkExists(form.values.email)
          setCurrentStep(exists ? 'enter_password' : 'create_password')
          break
        case 'enter_password':
          await login(form.values)
          onClose()
          showSuccessNotification('Logged in successfully.')
          break
        case 'create_password':
          await signup(form.values)
          onClose()
          showSuccessNotification('Account created successfully.')
          break
        case 'forgot_password':
          await sendResetPasswordEmail({ email: form.values.email })
          setCurrentStep('email_sent')
          showSuccessNotification('The reset password instructions have been sent.')
          break
        case 'create_new_password':
          if (!localResetParams) {
            throw new Error('Reset parameters are missing')
          }

          await resetPassword({
            newPassword: form.values.password,
            token: localResetParams.token,
            username: localResetParams.username
          })

          console.log('**************this should not show on fail')

          showSuccessNotification('Password has been reset successfully.')

          // Clear URL params and close modal
          window.history.replaceState({}, document.title, window.location.pathname)
          onClose()
          break
        case 'email_sent':
          // Just close the modal
          onClose()
          break
      }
    } catch (error: any) {
      console.error(error)
    }
  }

  const getStepTitle = (): string => {
    switch (currentStep) {
      case 'email':
        return 'Welcome to ETF Nexus'
      case 'enter_password':
        return 'Enter your password'
      case 'create_password':
        return 'Create a password'
      case 'forgot_password':
        return 'Reset password'
      case 'create_new_password':
        return 'Create a new password'
      case 'email_sent':
        return 'Email sent'
      default:
        return ''
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'email':
        return (
          <>
            <Text mt="md">Enter your email address to continue</Text>
            <TextInput size="lg" mt="xl" label="Email address" placeholder="address@email.com" {...form.getInputProps('email')} />
          </>
        )
      case 'enter_password':
        return (
          <>
            <Text mt="md">
              Enter your password for <span style={{ fontWeight: 600 }}>{form.values.email}</span> to finish logging in
            </Text>
            <PasswordInput size="lg" mt="xl" label="Enter password" {...form.getInputProps('password')} />
          </>
        )
      case 'create_password':
        return (
          <>
            <Text size="lg" mt="md">
              Create a password for {form.values.email} to finish signing up
            </Text>
            <PasswordInput size="lg" mt="xl" label="Password" {...form.getInputProps('password')} />
            {!form.errors.password && (
              <Text size="xs" c="dimmed" mt={4}>
                Minimum 8 characters required
              </Text>
            )}
          </>
        )
      case 'forgot_password':
        return (
          <>
            <Text mt="md">Please enter the email address associated with your account, and we'll send you a link to reset your password.</Text>
            <TextInput size="lg" mt="xl" label="Email address" {...form.getInputProps('email')} />
          </>
        )
      case 'email_sent':
        return (
          <>
            <Text mt="md">
              We sent an email to <span style={{ fontWeight: 600 }}>{form.values.email}</span> with a link to reset your password.
            </Text>
          </>
        )
      case 'create_new_password':
        return (
          <>
            <Text mt="md">
              Enter your new password for <span style={{ fontWeight: 600 }}>{localResetParams?.username}</span> to finish logging in.
            </Text>
            <PasswordInput size="lg" mt="xl" label="Create new password" placeholder="Enter your new password" {...form.getInputProps('password')} />
            {!form.errors.password && (
              <Text size="xs" c="dimmed" mt={4}>
                Minimum 8 characters required
              </Text>
            )}
            {/* 
            {localResetParams && (
              <Stack mt="md" gap="xs">
                <Text size="sm" fw={500}>
                  Reset parameters:
                </Text>
                <Code block>
                  {`Token: ${localResetParams.token}

                  Username: ${localResetParams.username}`}
                </Code>
              </Stack>
            )} */}
          </>
        )
    }
  }

  const resetModal = () => {
    setCurrentStep('email')
    setLocalResetParams(null)
    form.setValues({ email: '', password: '' })
    form.reset()
  }

  const handleModalClose = () => {
    // Clear URL params when closing the modal
    if (currentStep === 'create_new_password') {
      window.history.replaceState({}, document.title, window.location.pathname)
    }
    onClose()
    resetModal()
  }

  useEffect(() => {
    if (!opened) {
      resetModal()
    }
  }, [opened])

  return (
    <Modal
      opened={opened || currentStep === 'create_new_password'}
      onClose={handleModalClose}
      size="md"
      withCloseButton={false}
      padding={0}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
          padding: 0,
          margin: 0
        }
      }}>
      <Box px="lg" pt="lg" pb="md">
        <Group justify="space-between" align="center" w="100%">
          <Box w={20}>
            {shouldShowBackButton() && (
              <ActionIcon onClick={handleBack} variant="transparent">
                <IcArrowRight style={{ transform: 'rotate(180deg)', color: theme.colors.grey[8], width: '16px', height: '16px' }} />
              </ActionIcon>
            )}
            {shouldShowCloseButton() && (
              <ActionIcon onClick={handleModalClose} variant="transparent" tabIndex={-1} styles={{ root: { ':focus': { outline: 'none' } } }}>
                <IcX style={{ color: theme.colors.grey[8], width: '14px', height: '14px' }} />
              </ActionIcon>
            )}
          </Box>

          <Text fw={600} fz="lg">
            Sign up or Connect
          </Text>

          <Box w={20}></Box>
        </Group>
      </Box>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          handleNext()
        }}>
        <Stack px="3xl" pb="3xl" pt="xl" gap={0}>
          <Text fw={700} fz="xl" c="grey.8" className={styles.textWithStyleUnderScore}>
            {getStepTitle()}
          </Text>

          {renderStepContent()}

          {currentStep === 'enter_password' && (
            <UnstyledButton onClick={() => setCurrentStep('forgot_password')} mt="md">
              <Text fw={600} td="underline" c="blue.5">
                Forgot password?
              </Text>
            </UnstyledButton>
          )}

          {currentStep !== 'email_sent' && (
            <Button type="submit" loading={loading} size="md" mt="xl">
              <Text fw={600} c="white">
                {(() => {
                  switch (currentStep) {
                    case 'email':
                      return 'Continue'
                    case 'enter_password':
                      return 'Log in'
                    case 'create_password':
                      return 'Sign up'
                    case 'forgot_password':
                      return 'Send reset link'
                    case 'create_new_password':
                      return 'Reset Password'
                    default:
                      return 'Next'
                  }
                })()}
              </Text>
            </Button>
          )}

          {currentStep === 'create_password' && (
            <Text mt="xl" c="grey.8" ta="center" style={{ whiteSpace: 'pre-line' }}>
              By creating an account, you agree to our{' '}
              <Anchor fw={600} href="#">
                Terms of Service
              </Anchor>{' '}
              and{' '}
              <Anchor fw={600} href="#">
                Privacy Policy
              </Anchor>
            </Text>
          )}
        </Stack>
      </form>
    </Modal>
  )
}

export default AuthModal
